import { isContentfulString, isEmptyArrayOrObject } from '@philips-internal/oswald-ui-utils';
import { EditorService } from '../editor-service/index.js';
const GLOBAL_LOCALE = 'global';
const LOCALE_PATTERN = /^[a-z]{2}-[a-z]{2}$/;
const SERVICE_NOT_INITIALIZED_MESSAGE = 'Service has not been initialized yet.';
/**
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/resolvedOptions#locale
 */
function getBrowserLocale() {
    return normalizeLocale(Intl.NumberFormat().resolvedOptions().locale);
}
function getChosenLocale(defaultLocale, locale, supportedLocales) {
    if (supportedLocales.includes(locale)) {
        return locale;
    }
    return defaultLocale;
}
function isFollowingLocalePattern(locale) {
    return LOCALE_PATTERN.test(locale);
}
function localeError(message) {
    return new Error(`[Locale Service]: ${message}`);
}
function normalizeLocale(locale) {
    return locale.toLowerCase();
}
function createLocaleService() {
    let currentLocale;
    let defaultLocale;
    let initialized = false;
    let supportedLocales;
    const listeners = new Set();
    const browserLocale = getBrowserLocale();
    currentLocale = browserLocale;
    defaultLocale = browserLocale;
    const addListener = (fn) => {
        if (!initialized) {
            throw localeError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        if (listeners.has(fn)) {
            throw localeError('');
        }
        listeners.add(fn);
        return () => {
            listeners.delete(fn);
        };
    };
    const getCurrentLocale = () => {
        return currentLocale;
    };
    const getCurrentLocaleCountry = () => {
        const [, currentCountry] = getCurrentLocale().split('-');
        return currentCountry;
    };
    const getDefaultLocale = () => {
        return defaultLocale;
    };
    const getSupportedLocales = () => {
        if (!initialized) {
            throw localeError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        return supportedLocales;
    };
    const hasBeenInitialized = () => {
        return initialized;
    };
    const initialize = async () => {
        if (initialized) {
            throw localeError('Service already initialized.');
        }
        // @ts-expect-error
        const { get } = await import('virtual:oswald-configuration');
        const runtime = await get();
        const configuration = runtime.i18n.locales;
        if (!isContentfulString(configuration.defaultLocale)) {
            throw localeError(`A default locale is required.`);
        }
        if (!isFollowingLocalePattern(configuration.defaultLocale)) {
            throw localeError(`The default locale is not following any recognizable pattern (${LOCALE_PATTERN}).`);
        }
        if (isEmptyArrayOrObject(configuration.supportedLocales)) {
            throw localeError(`A list of supported locales is required.`);
        }
        if (!configuration.supportedLocales.some(supported => isFollowingLocalePattern(supported))) {
            throw localeError(`One or more of the supported locales is not following any recognizable pattern (${LOCALE_PATTERN}).`);
        }
        if (!configuration.supportedLocales.some(supported => supported === configuration.defaultLocale)) {
            throw localeError(`The default locale is not present in the list of supported locales.`);
        }
        defaultLocale = configuration.defaultLocale;
        supportedLocales = configuration.supportedLocales;
        initialized = true;
        const browserLocale = getBrowserLocale();
        setCurrentLocale(browserLocale);
    };
    const isLocaleSupported = (locale) => {
        if (!initialized) {
            throw localeError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        return supportedLocales.includes(locale);
    };
    const setCurrentLocale = (locale) => {
        if (!initialized) {
            throw localeError(SERVICE_NOT_INITIALIZED_MESSAGE);
        }
        locale = locale.toLowerCase();
        if (!isFollowingLocalePattern(locale)) {
            if (!EditorService.isEditor()) {
                throw localeError(`Locale ${locale} is not following any recognizable pattern (${LOCALE_PATTERN}).`);
            }
            currentLocale = GLOBAL_LOCALE;
            notifyListeners();
            return;
        }
        currentLocale = getChosenLocale(defaultLocale, locale, supportedLocales);
        notifyListeners();
    };
    function notifyListeners() {
        for (const listener of listeners) {
            listener(currentLocale);
        }
    }
    return {
        addListener,
        getCurrentLocale,
        getCurrentLocaleCountry,
        getDefaultLocale,
        getSupportedLocales,
        hasBeenInitialized,
        initialize,
        isLocaleSupported,
        setCurrentLocale,
    };
}
const LocaleService = createLocaleService();
export { GLOBAL_LOCALE, LocaleService, };
//# sourceMappingURL=locale-service.js.map